import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Button, Modal, Radio, Text, Alert, tokens } from '@hotjar/hotsauce';
import { useForm, FormProvider } from 'react-hook-form';
import { Global, css } from '@emotion/react';

import { useTranslationWithPrefix } from '@hotjar/translations';
import { DisableAngularModalBackdrop } from '@hotjar/webapp/components/DisableAngularModalBackdrop/DisableAngularModalBackdrop';
import type { PaymentMethod, VaultedPaymentMethod } from '@hotjar/webapp/billing/payment';
import { BillingCycleSelectorWithProvider } from '@hotjar/checkout/src/PaymentDetails/components/BillingCycleSelector';
import { useDisplayCosts } from '@hotjar/checkout/src/PaymentDetails/utils/useDisplayCosts';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { extractAccountGroupingProps } from '@hotjar/webapp/tracking/utils';
import {
  BILLING_CYCLE_EVENT,
  PAYMENT_METHOD_EVENT,
  SET_PAYMENT_DETAILS,
  trackPaymentFormSubmitted,
  trackPaymentModalLoaded,
} from '@hotjar/checkout/src/utils/tracking';
import { getErrorMessageTranslationKey } from '@hotjar/checkout/src/braintree/error-messages';
import type { PaymentDetailsErrorMessage } from '@hotjar/checkout/src/braintree/error-messages';
import { paymentMethodAPI } from '@hotjar/webapp/domains/billing/paymentMethod';
import { PAYMENT_ORIGINS } from '@hotjar/webapp/domains/billing/constants';
import { createGlobalModal } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import type { PaymentDetailsProps } from '@hotjar/checkout/src/PaymentDetails/types';
import * as Sentry from '@sentry/browser';

import { CreditCardForm, CC_FORM_ID } from './components/CreditCardForm/CreditCardForm';
import { PayPalLogo } from './assets/PayPalLogo';
import { PayPal, PAYPAL_FORM_ID } from './components/PayPal/PayPal';
import { AgreeNotice } from './components/AgreeNotice/AgreeNotice';
import type { PaymentMethodType } from './types';

const S = {
  Section: styled.div`
    ${tokens.flexContainer('form')};

    :first-of-type {
      margin-bottom: ${tokens.spacer.x4};
    }
  `,
  AgreeNotice: styled(AgreeNotice)`
    margin-top: ${tokens.spacer.x3};
  `,
  PaymentMethodSelector: styled.div`
    ${tokens.flexContainer('default', { gap: tokens.spacer.x4 })};
  `,

  PayPalLogo: styled(PayPalLogo)`
    margin-top: 2px;
  `,
};

export interface OnPaymentMethodVaultedReturn {
  vaultedPaymentMethod: VaultedPaymentMethod;
  billingCycle?: IBillingCycle;
}

interface SubscriptionUpsertError {
  error: string;
}

function checkIsSubscriptionUpsertError(error: unknown): error is SubscriptionUpsertError {
  return typeof error === 'object' && error !== null && 'error' in error;
}

export function LegacyPaymentDetails({
  account,
  costSpecs,
  initialBillingCycle,
  referrer,
  onCancel,
  onPaymentMethodVaulted,
  onSubmit,
  initialErrorMessage,
}: PaymentDetailsProps & GlobalModalProps<OnPaymentMethodVaultedReturn, string>): JSX.Element {
  const { t: tActions } = useTranslationWithPrefix('common:actions');
  const { t: tStates } = useTranslationWithPrefix('common:states');
  const { t: tBilling } = useTranslationWithPrefix('billing:payment-details');
  const [modalLoaded, setModalLoaded] = useState(false);
  const [billingCycle, setBillingCycle] = useState<IBillingCycle | undefined>(
    initialBillingCycle ?? account.billingCycle,
  );
  const { monthlyCosts, yearlyCosts, disclaimerDisplayCosts } = useDisplayCosts(costSpecs);
  const [paymentMethodType, setPaymentMethodType] = useState<PaymentMethodType>('credit_card');
  const [errorMessage, setErrorMessage] = useState(
    initialErrorMessage ? `${tBilling('form.error-payment-failed')}: ${initialErrorMessage}` : '',
  );
  const [isLoading, setIsLoading] = useState(false);
  const { click } = useTrack();
  const countryIsoCode = account?.location?.split(',')[1];

  const trackOnSubmitClicked = (type: PaymentMethodType) => {
    click(
      SET_PAYMENT_DETAILS,
      `${PAYMENT_ORIGINS.updatePaymentDetails} with ${type}`,
      extractAccountGroupingProps(account),
    );
  };

  const handleBillingCycleSelection = (cycle: IBillingCycle) => {
    setBillingCycle(cycle);
    click(BILLING_CYCLE_EVENT, cycle, extractAccountGroupingProps(account));
  };

  const handlePaymentMethodTypeChange = (methodType: PaymentMethodType) => {
    setPaymentMethodType(methodType);
    click(PAYMENT_METHOD_EVENT, methodType, extractAccountGroupingProps(account));
  };

  async function handleVaultedPaymentMethod(paymentMethod: PaymentMethod) {
    try {
      setIsLoading(true);
      const vaultedPaymentMethod = await paymentMethodAPI.vaultCard(
        account?.id as number,
        paymentMethod,
      );

      await onPaymentMethodVaulted?.({ vaultedPaymentMethod, billingCycle });

      onSubmit({ vaultedPaymentMethod, billingCycle });
      trackPaymentFormSubmitted(account, true);
    } catch (error: unknown) {
      let message = '';
      // right now we have errors coming in two forms depending on the new/old flow
      //  - simple string (new flow)
      //  - nonstandar object { error: 'error key' } (old flow)
      // TODO: simplify after migrating the whole checkout flow
      if (checkIsSubscriptionUpsertError(error)) {
        message = error.error;
      }

      if (typeof error === 'string') {
        message = error;
      }
      const errorKey = getErrorMessageTranslationKey(
        message as PaymentDetailsErrorMessage,
        'error',
      );

      // let's trigger a warning when we have unexpected errors
      if (errorKey === 'error.unknown') {
        Sentry.captureException(new Error(message));
      }

      setErrorMessage(tBilling(errorKey));
      trackPaymentFormSubmitted(account, false, message);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    // NOTE: modalLoaded check is to stop the bleeding of multiple events for now
    // TODO: if there is a way to not have to do it, do that instead :)
    if (account && modalLoaded) {
      trackPaymentModalLoaded(account, referrer);
      setModalLoaded(true);
    }
  }, [account, referrer, modalLoaded]);

  /**
   * for the same reason as on line 148 the legacy controller
   * only assigns initialBillingCycle after doing some async opperations
   * which means billingCycle will never receive initialBillingCycle on
   * useState(initialBillingCycle) the first time, so we force the assignment
   * as soon as it gets passed to the props
   * TODO: this should be removed and done correctly when we clean up experiment code
   */
  useEffect(() => {
    setBillingCycle(initialBillingCycle ?? account.billingCycle);
  }, [initialBillingCycle, account]);

  const showBillingCycleSelector =
    initialBillingCycle !== 'yearly_recurring' && monthlyCosts?.hasPayableAmount;

  return (
    <>
      <DisableAngularModalBackdrop />
      <Global
        styles={css`
          #Cardinal-ElementContainer {
            position: relative;
            z-index: 9999999;
          }
        `}
      />
      <Modal contentLabel={tBilling('title')} isOpen onClose={onCancel}>
        <Modal.Header title={tBilling('title')} />
        <Modal.Body>
          <FormProvider {...useForm()}>
            {showBillingCycleSelector && (
              <S.Section>
                <Text textType="subtitle1">{tBilling('billing-cycle.title')}</Text>
                <BillingCycleSelectorWithProvider
                  yearlyAmounts={yearlyCosts as IBillingAmounts}
                  monthlyAmounts={monthlyCosts as IBillingAmounts}
                  billingCycle={
                    /**
                     * having the account.hasPaidPlan here creates an edge case where you'll see the billing cycle selector without a pre-selection which should technically not be the case. This happens with the following steps:
                     *
                     * 1. you churned
                     * 2. deleted the payment method and;
                     * 3. cancel downgrade
                     *
                     * ☝️ this is the same behavior as what we had before this change so I am happy to leave it as is for now
                     */
                    account.isCustomer && account.hasPaidPlan ? billingCycle : undefined
                  }
                  onChange={handleBillingCycleSelection}
                />
              </S.Section>
            )}
            <S.Section>
              <Text textType="subtitle1">{tBilling('payment-method.title')}</Text>
              {errorMessage && <Alert kind="danger">{errorMessage}</Alert>}
              <S.PaymentMethodSelector>
                <Radio
                  labelContent="Credit Card"
                  name="select-payment-method"
                  onChange={() => handlePaymentMethodTypeChange('credit_card')}
                  value="credit_card"
                  isChecked={paymentMethodType === 'credit_card'}
                />
                <Radio
                  labelContent={<S.PayPalLogo />}
                  name="select-payment-method"
                  onChange={() => handlePaymentMethodTypeChange('paypal')}
                  value="paypal"
                  isChecked={paymentMethodType === 'paypal'}
                />
              </S.PaymentMethodSelector>
              <CreditCardForm
                visible={paymentMethodType === 'credit_card'}
                onSuccess={handleVaultedPaymentMethod}
                onSubmit={() => trackOnSubmitClicked('credit_card')}
                setErrorMessage={setErrorMessage}
                setIsLoading={setIsLoading}
              />
              <PayPal
                visible={paymentMethodType === 'paypal'}
                onSuccess={handleVaultedPaymentMethod}
                onSubmit={() => trackOnSubmitClicked('paypal')}
              />
            </S.Section>
            <S.AgreeNotice
              billingCycle={billingCycle}
              paymentMethodType={paymentMethodType}
              countryIsoCode={countryIsoCode}
              disclaimerDisplayCosts={disclaimerDisplayCosts}
            />
          </FormProvider>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => onCancel()} kind="ghost" isDisabled={isLoading}>
            {tActions('cancel')}
          </Button>
          <Button
            loadingText={tStates('loading')}
            type="submit"
            form={paymentMethodType === 'paypal' ? PAYPAL_FORM_ID : CC_FORM_ID}
            isLoading={isLoading}
          >
            {tBilling('actions.submit')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export const openLegacyPaymentDetails = createGlobalModal<
  PaymentDetailsProps,
  OnPaymentMethodVaultedReturn,
  string
>(LegacyPaymentDetails);
